import { type FC } from "react"
import { useTranslation } from "react-i18next"
import { Stack } from "@mui/material"

import type { DeviceGroupRef } from "types/group.types"
import OverflowChip, { OverflowItem } from "widgets/common/OverflowChip"
import { GroupChip } from "./GroupChip"

const VISIBLE_GROUPS = 2

interface GroupChipsProps {
  groups: DeviceGroupRef[]
  // One line, the chips shrinking to fit, as a table cell needs.
  noWrap?: boolean
}

const GroupChips: FC<GroupChipsProps> = ({ groups, noWrap = false }) => {
  const { t } = useTranslation()

  const overflowGroups = groups.slice(VISIBLE_GROUPS)

  return (
    <Stack
      direction="row"
      gap="0.5rem"
      flexWrap={noWrap ? "nowrap" : "wrap"}
      sx={
        noWrap
          ? {
              alignItems: "center",
              // As a flex item the chip would take the link's underline.
              "& > a": { minWidth: 0, display: "flex", textDecoration: "none" },
              "& > a .MuiChip-root": { maxWidth: "min(150px, 100%)" },
            }
          : undefined
      }
    >
      {groups.slice(0, VISIBLE_GROUPS).map((group) => (
        <GroupChip key={group.id} group={group} />
      ))}
      {overflowGroups.length > 0 && (
        <OverflowChip
          count={overflowGroups.length}
          ariaLabel={t("device_groups.MORE_GROUPS", { count: overflowGroups.length })}
          data-testid="overflow-chip"
        >
          {overflowGroups.map((group) => (
            <OverflowItem key={group.id}>
              <GroupChip group={group} />
            </OverflowItem>
          ))}
        </OverflowChip>
      )}
    </Stack>
  )
}

export default GroupChips
